import type { ByteSource } from './bytes';
import { Iso9660 } from './iso9660';
import { MpqArchive } from './mpq/archive';
import { compareVersions, PatchArchive } from './patch';
import { gameVersionOf, peFileVersion } from './pe';
import { ArchiveSet } from './vfs';

/**
 * Game files in priority order (highest first).
 *
 *  - Two Blizzard patch executables, applied on top of the archives in memory: `D2Patch_114d.exe`
 *    (1.14d, the realm's default version) and the LoD 1.09d patch (`PATCHES/LOD_109D.EXE` on the
 *    LoD disc, or the `LODPatch_109d.exe` download). One of them is used at a time.
 *  - `patch_d2.mpq`: the patch archive of an installed game (the Blizzard digital download ships
 *    it at 1.14d). Mounted as the top archive when the game is read as 1.14d and no patch
 *    executable is given.
 *  - The eight base archives of the discs, which the digital download carries under the same names.
 *  - `game.exe`, read only for its version, which names the version of a `patch_d2.mpq` beside it.
 *
 * Names are matched case-insensitively wherever they sit (ISO, folder, sub-folder).
 */
export const ARCHIVE_ORDER = [
  'd2patch_114d.exe',
  'lod_109d.exe',
  'patch_d2.mpq',
  'd2exp.mpq',
  'd2xmusic.mpq',
  'd2xtalk.mpq',
  'd2data.mpq',
  'd2char.mpq',
  'd2sfx.mpq',
  'd2speech.mpq',
  'd2music.mpq',
  'game.exe',
] as const;
export type ArchiveName = (typeof ARCHIVE_ORDER)[number];

/** The game versions a mount can be asked for: `D2_PATCH` on the realm, `?patch=` in the browser. */
export const PATCH_TARGETS = ['1.14d', '1.09d', 'off'] as const;
export type PatchTarget = (typeof PATCH_TARGETS)[number];
export const DEFAULT_PATCH_TARGET: PatchTarget = '1.14d';

/** The patch executable that brings the discs to each target version. */
export const PATCH_EXE: Record<Exclude<PatchTarget, 'off'>, ArchiveName> = { '1.14d': 'd2patch_114d.exe', '1.09d': 'lod_109d.exe' };

/** An installed game's patch archive (1.10 and later keep every patched file in it). */
export const INSTALLED_PATCH: ArchiveName = 'patch_d2.mpq';

/** Names that are not MPQ archives of game data (patch executables, the version probe). */
const NOT_MPQ: readonly ArchiveName[] = ['d2patch_114d.exe', 'lod_109d.exe', 'game.exe'];

/** Other names the same files are distributed under (the Battle.net downloads). */
const ALIASES: Record<string, ArchiveName> = { 'lodpatch_109d.exe': 'lod_109d.exe', 'lodpatch_114d.exe': 'd2patch_114d.exe' };

/** Archives without which the game can't start (LoD). */
export const REQUIRED_ARCHIVES: ArchiveName[] = ['d2data.mpq', 'd2char.mpq', 'd2sfx.mpq', 'd2exp.mpq'];

/** Files that belong to a Diablo II install or disc but are never used, with the reason shown to the player. */
export const IGNORED_FILES: Record<string, string> = {
  'd2video.mpq': 'Cinematics. Not needed.',
  'd2xvideo.mpq': 'Cinematics. Not needed.',
  'diablo ii patch (carbon)': 'Patch data of the Mac game. Not used: this engine reads the Windows patches (D2Patch_114d.exe).',
};

/** Other Blizzard patch executables people may have: recognised so the import can say why they don't help. */
const OTHER_PATCH = /^(lodpatch|d2patch)_?(\d)(\d\d)([a-z]?)\.exe$/;

/** The Mac patch installer's data archive, versioned in its name (`D2XP_XMAC_1xx_113b.mpq`). */
const MAC_PATCH = /^d2xp_xmac_\w+_(\d)(\d\d)([a-z]?)\.mpq$/;

/**
 * Parses a requested version: `1.14d` (also `114`, `on`, empty: the default), `1.09d` (`109`),
 * or the discs' own data (`off`, `0`, `false`, `no`, `1.07`). Anything else gives the default.
 */
export function parsePatchTarget(v: string | null | undefined): PatchTarget {
  const s = (v ?? '').trim().toLowerCase();
  if (/^(off|0|false|no|none|1\.?07)$/.test(s)) return 'off';
  if (/^1\.?09d?$/.test(s)) return '1.09d';
  return DEFAULT_PATCH_TARGET;
}

/** True when `v` names a known target (so callers can warn about typos). */
export function isPatchTargetText(v: string): boolean {
  return /^(|on|1\.?14d?|off|0|false|no|none|1\.?07|1\.?09d?)$/i.test(v.trim());
}

/** The game version a target reads as. */
export function targetVersion(t: PatchTarget): string {
  return t === 'off' ? BASE_VERSION : t;
}

/**
 * True when two versions carry the same game data. 1.14a–1.14d changed the executable only:
 * the 1.14b digital installer's data matches 1.14d's in every part the realm compares (the
 * per-part check at login still has the last word).
 */
export function sameGameData(a: string, b: string): boolean {
  return a === b || (/^1\.14/.test(a) && /^1\.14/.test(b));
}

export interface NamedSource {
  name: ArchiveName;
  source: ByteSource;
  /** Where it came from, for the import UI ("Diablo II install.iso → D2DATA.MPQ"). */
  origin: string;
}

/** A recognised file that is not mounted. */
export interface IgnoredFile {
  /** Lower-case file name. */
  name: string;
  origin: string;
  reason: string;
}

export function archiveNameOf(fileName: string): ArchiveName | null {
  const base = baseName(fileName);
  if ((ARCHIVE_ORDER as readonly string[]).includes(base)) return base as ArchiveName;
  return ALIASES[base] ?? null;
}

/** Why a recognised Diablo II file is never used, or null if it may be used / is not a game file. */
export function ignoredReason(fileName: string): string | null {
  const base = baseName(fileName);
  if (archiveNameOf(base)) return null;
  if (IGNORED_FILES[base]) return IGNORED_FILES[base];
  const mac = MAC_PATCH.exec(base);
  if (mac) return `Mac patch ${mac[1]}.${mac[2]}${mac[3]}: a different version. Not used: realms here play 1.14d (D2Patch_114d.exe).`;
  const m = OTHER_PATCH.exec(base);
  if (m) {
    const v = `${m[2]}.${m[3]}${m[4]}`;
    return m[1] === 'd2patch' && compareVersions(v, '1.14') < 0
      ? `Patch ${v} for classic Diablo II. Lord of Destruction needs D2Patch_114d.exe (or LODPatch_109d.exe for 1.09d).`
      : `Patch ${v}. Not used: realms here play 1.14d (D2Patch_114d.exe) or 1.09d (LODPatch_109d.exe).`;
  }
  return null;
}

function baseName(fileName: string): string {
  return fileName.split(/[\\/]/).pop()!.toLowerCase();
}

/**
 * Finds every game archive (and the patches) on a disc image. Recognised files that are not used
 * (video archives, other patches) are appended to `ignored` when given.
 */
export async function archivesInIso(src: ByteSource, label: string, ignored?: IgnoredFile[]): Promise<NamedSource[]> {
  const iso = await Iso9660.open(src);
  const out: NamedSource[] = [];
  for (const e of await iso.walk(undefined, 2)) {
    const name = archiveNameOf(e.name);
    if (name) out.push({ name, source: iso.open(e), origin: `${label} → ${e.path}` });
    else {
      const reason = ignoredReason(e.name);
      if (reason && ignored) ignored.push({ name: baseName(e.name), origin: `${label} → ${e.path}`, reason });
    }
  }
  return out;
}

export interface MountOptions {
  /**
   * The version to read the game as (default 1.14d). `true` is the default, `false` is `'off'`
   * (the discs' own 1.07 data).
   */
  patch?: boolean | PatchTarget;
  /** Recognised files that were found but are not mounted (reported back in `ignored`). */
  ignored?: IgnoredFile[];
}

/** What happened to the patch: one applied, none given, turned off, or found but unusable. */
export type PatchState = 'applied' | 'absent' | 'disabled' | 'skipped';

export interface MountReport {
  vfs: ArchiveSet;
  mounted: { name: ArchiveName; origin: string; files: number }[];
  missing: ArchiveName[];
  /** The version that was asked for. */
  target: PatchTarget;
  patch: PatchState;
  /** The file that makes the data its version (a patch executable or `patch_d2.mpq`), if any. */
  patchFile?: ArchiveName;
  /** Why a patch that was found is not applied (wrong disc version, unreadable…). */
  patchSkipped?: string;
  /** Which patch `patchSkipped` is about. */
  skippedFile?: ArchiveName;
  /** Version the data is read as: "1.14d", "1.09d", an installed patch's version, else the discs' "1.07". */
  version: string;
  /** How the version of an installed `patch_d2.mpq` was told ("Game.exe 1.14.3.71", or assumed). */
  versionNote?: string;
  /** Language folder of the string tables ("eng", "deu"…), null if none was found. */
  language: string | null;
  ignored: IgnoredFile[];
  /** The sources the mount reads from (others may be closed by the caller). */
  used: ByteSource[];
}

/** Version of the archives without a patch (every LoD disc and the base MPQs of the digital install). */
export const BASE_VERSION = '1.07';

/**
 * Opens the archives (one per name; first one wins) in priority order, with the patch for the
 * target version on top:
 *
 *  - **1.14d**: `D2Patch_114d.exe` over the base archives (its deltas are checked against them by
 *    CRC); else an installed `patch_d2.mpq` as the top archive (its version from `Game.exe` when
 *    present, else taken to be 1.14d: the realm's per-part data check has the last word); else
 *    the 1.09d patch, so a copy without the 1.14d patch still runs (the report says so).
 *  - **1.09d**: the 1.09d patch only; `patch_d2.mpq` and the 1.14d patch are reported unused.
 *  - **off**: the discs' own 1.07 data.
 */
export async function mountArchives(sources: NamedSource[], opts: MountOptions = {}): Promise<MountReport> {
  const target: PatchTarget = opts.patch === false ? 'off' : opts.patch === true || opts.patch === undefined ? DEFAULT_PATCH_TARGET : opts.patch;
  const byName = new Map<ArchiveName, NamedSource>();
  for (const s of sources) if (!byName.has(s.name)) byName.set(s.name, s);
  const archives: MpqArchive[] = [];
  const mounted: MountReport['mounted'] = [];
  const used: ByteSource[] = [];
  for (const name of ARCHIVE_ORDER) {
    const s = byName.get(name);
    if (!s || NOT_MPQ.includes(name) || name === INSTALLED_PATCH) continue;
    const a = await MpqArchive.open(s.source, name);
    archives.push(a);
    mounted.push({ name, origin: s.origin, files: a.blocks.length });
    used.push(s.source);
  }
  const missing = REQUIRED_ARCHIVES.filter((n) => !byName.has(n));
  const ignored = [...(opts.ignored ?? [])];
  const unused = (name: ArchiveName, reason: string) => {
    const s = byName.get(name);
    if (s) ignored.push({ name, origin: s.origin, reason });
  };

  const base = new ArchiveSet(archives);
  let skip: { file: ArchiveName; reason: string } | undefined;
  /** Opens a patch executable and checks that it fits these archives. */
  const tryExe = async (name: ArchiveName, version: string): Promise<PatchArchive | null> => {
    const s = byName.get(name);
    if (!s) return null;
    let reason: string;
    try {
      const p = await PatchArchive.open(s.source, name);
      if (!p.forExpansion) reason = 'not a Lord of Destruction patch';
      else if (p.version && !p.version.startsWith(version)) reason = `it is patch ${p.version}`;
      else if (!byName.has('d2exp.mpq')) reason = 'needs the Lord of Destruction archives';
      else if (!(await p.appliesTo((path, h) => base.deltaBase(path, h)))) reason = 'made for a different version of the discs';
      else {
        mounted.unshift({ name, origin: s.origin, files: p.paths().length });
        used.push(s.source);
        return p;
      }
    } catch (e) {
      reason = e instanceof Error ? e.message : String(e);
    }
    skip ??= { file: name, reason };
    return null;
  };

  let patch: PatchArchive | null = null;
  let installed: { archive: MpqArchive; version: string; note: string } | null = null;
  let patchFile: ArchiveName | undefined;
  if (target === '1.14d') {
    patch = await tryExe(PATCH_EXE['1.14d'], '1.14');
    if (patch) {
      patchFile = PATCH_EXE['1.14d'];
      unused(INSTALLED_PATCH, 'Installed patch. Not needed: D2Patch_114d.exe is applied to the original archives instead.');
    } else if (byName.has(INSTALLED_PATCH)) {
      installed = await openInstalled(byName.get(INSTALLED_PATCH)!, byName.get('game.exe'));
      patchFile = INSTALLED_PATCH;
      mounted.unshift({ name: INSTALLED_PATCH, origin: byName.get(INSTALLED_PATCH)!.origin, files: installed.archive.blocks.length });
      used.push(byName.get(INSTALLED_PATCH)!.source);
    } else {
      patch = await tryExe(PATCH_EXE['1.09d'], '1.09');
      if (patch) patchFile = PATCH_EXE['1.09d'];
    }
  } else if (target === '1.09d') {
    patch = await tryExe(PATCH_EXE['1.09d'], '1.09');
    if (patch) patchFile = PATCH_EXE['1.09d'];
    unused(PATCH_EXE['1.14d'], 'Patch 1.14d. Not used: the game is read as 1.09d here.');
    unused(INSTALLED_PATCH, 'Installed patch (1.10 or later). Not used: the game is read as 1.09d here.');
  } else {
    unused(PATCH_EXE['1.14d'], 'Patch 1.14d. Not used: patching is turned off.');
    unused(INSTALLED_PATCH, 'Installed patch (1.10 or later). Not used: patching is turned off.');
  }

  const anyPatch = byName.has(PATCH_EXE['1.14d']) || byName.has(PATCH_EXE['1.09d']) || byName.has(INSTALLED_PATCH);
  const state: PatchState = patchFile ? 'applied' : target === 'off' && anyPatch ? 'disabled' : skip ? 'skipped' : 'absent';
  const vfs = new ArchiveSet(installed ? [installed.archive, ...archives] : archives, patch);
  if (installed) vfs.version = installed.version;
  return {
    vfs,
    mounted,
    missing,
    target,
    patch: state,
    patchFile,
    patchSkipped: target === 'off' ? (anyPatch ? 'disabled' : undefined) : skip?.reason,
    skippedFile: target === 'off' ? undefined : skip?.file,
    version: vfs.version ?? BASE_VERSION,
    versionNote: installed?.note,
    language: detectLanguage(vfs),
    ignored,
    used,
  };
}

/** Opens an installed `patch_d2.mpq`; its version is `Game.exe`'s when that is given. */
async function openInstalled(s: NamedSource, exe: NamedSource | undefined): Promise<{ archive: MpqArchive; version: string; note: string }> {
  const archive = await MpqArchive.open(s.source, INSTALLED_PATCH);
  const v = exe ? await peFileVersion(exe.source).catch(() => null) : null;
  if (v) return { archive, version: gameVersionOf(v), note: `Game.exe ${v.join('.')}` };
  return { archive, version: '1.14d', note: 'assumed: no Game.exe beside patch_d2.mpq' };
}

/** Language folders of `data\local\lng`, English first. */
export const LANGUAGES = ['eng', 'deu', 'fra', 'ita', 'esp', 'pol', 'kor', 'chi', 'jpn', 'por', 'rus'] as const;

/**
 * The language of the player's copy: the first language whose Lord of Destruction strings
 * (`expansionstring.tbl`) are present, else the first with classic strings. A localized LoD
 * disc carries only its own language's expansion strings, while the classic archives may hold
 * several languages' `string.tbl`.
 */
export function detectLanguage(vfs: Pick<ArchiveSet, 'has'>): string | null {
  for (const l of LANGUAGES) if (vfs.has(`data\\local\\lng\\${l}\\expansionstring.tbl`)) return l;
  for (const l of LANGUAGES) if (vfs.has(`data\\local\\lng\\${l}\\string.tbl`)) return l;
  return null;
}

/** Plain-words summary of a mount for the import screen and error messages. */
export interface MountSummary {
  /** "Lord of Destruction 1.14d" */
  title: string;
  /** Problems that stop the game from starting or from joining a realm of the target version. */
  problems: string[];
  /** What to do about them. */
  advice: string[];
  /** Things worth knowing that are not problems. */
  notes: string[];
}

export function describeMount(
  r: Pick<MountReport, 'missing' | 'patch' | 'patchSkipped' | 'version' | 'ignored'> &
    Partial<Pick<MountReport, 'target' | 'patchFile' | 'skippedFile' | 'versionNote' | 'mounted'>>,
  labels: Partial<Record<ArchiveName, string>> = {},
): MountSummary {
  const problems: string[] = [];
  const advice: string[] = [];
  const notes: string[] = [];
  const name = (n: ArchiveName) => labels[n] ?? n;
  const target = r.target ?? DEFAULT_PATCH_TARGET;
  // Archives read out of Blizzard's digital installer (`Installer Tome.mpq`).
  const installer = !!r.mounted?.some((m) => /installer[ _-]?tome/i.test(m.origin));
  if (r.missing.length) {
    problems.push(`Missing: ${r.missing.map(name).join(', ')}.`);
    const lodOnly = r.missing.includes('d2exp.mpq') && r.missing.length === 1;
    const classicOnly = !r.missing.includes('d2exp.mpq');
    advice.push(
      installer
        ? lodOnly
          ? "Add Installer Tome.mpq from the Lord of Destruction installer (D2ES), or the Lord of Destruction disc image."
          : classicOnly
            ? 'Add Installer Tome.mpq from the Diablo II installer (D2), or the Diablo II Install and Play disc images.'
            : 'Add Installer Tome.mpq from both installers (Diablo II and Lord of Destruction).'
        : lodOnly
          ? 'Add the Lord of Destruction disc image, or d2exp.mpq from your Diablo II folder.'
          : 'Add the Diablo II Install and Play disc images, or the .mpq files from your Diablo II folder.',
    );
  }
  const digital = installer || r.ignored.some((f) => f.name === INSTALLED_PATCH) || r.patchFile === INSTALLED_PATCH;
  const want = targetVersion(target);
  if (target !== 'off' && r.version !== want && sameGameData(r.version, want)) {
    notes.push(
      `Your files are ${r.version}. Realms here play ${want}, which has the same game data, so you don't need the patch. ` +
        `If Battle.net ever says your files differ, add Blizzard's D2Patch_114d.exe and import again.`,
    );
  } else if (target !== 'off' && r.version !== want) {
    const exe = PATCH_EXE[target];
    if (r.skippedFile === exe) {
      problems.push(`The ${want} patch was found but not applied: ${r.patchSkipped}.`);
      advice.push(
        /different version/.test(r.patchSkipped ?? '')
          ? target === '1.14d'
            ? 'D2Patch_114d.exe patches the original files: use the archives from your discs, or the base .mpq files of your Diablo II folder.'
            : 'Your Lord of Destruction archive (d2exp.mpq) is not the one the patch was made for. Use the d2exp.mpq from your LoD disc, or the base files of your Diablo II folder.'
          : target === '1.14d'
            ? "Use Blizzard's D2Patch_114d.exe."
            : 'Use LOD_109D.EXE from the LoD disc or the official LODPatch_109d.exe.',
      );
    } else if (r.patchFile === INSTALLED_PATCH) {
      problems.push(`Your installed patch (patch_d2.mpq) is ${r.version}.`);
      advice.push(
        installer
          ? `Battle.net realms here play ${want}. Add Blizzard's D2Patch_114d.exe next to your installer files.`
          : `Battle.net realms here play ${want}. Update your Diablo II installation, or add Blizzard's D2Patch_114d.exe.`,
      );
    } else if (target === '1.14d') {
      problems.push(`No 1.14d patch: the game will run as ${r.version}.`);
      advice.push(
        digital
          ? "Battle.net realms here play 1.14d. Add Blizzard's patch file D2Patch_114d.exe next to your Diablo II files."
          : "Battle.net realms here play 1.14d. Add Blizzard's patch file D2Patch_114d.exe next to your disc images.",
      );
    } else {
      problems.push(`No 1.09d patch: the game will run as ${r.version}.`);
      advice.push(
        digital
          ? 'This realm plays 1.09d. Add the official patch file LODPatch_109d.exe next to your Diablo II files.'
          : 'This realm plays 1.09d. Your LoD disc has it in PATCHES\\LOD_109D.EXE; otherwise add the official LODPatch_109d.exe.',
      );
    }
  }
  if (r.patchFile === INSTALLED_PATCH && r.versionNote?.startsWith('assumed'))
    notes.push(`patch_d2.mpq is read as ${r.version}: add Game.exe from the same folder to confirm its version.`);
  const mac = r.ignored.find((f) => MAC_PATCH.test(f.name));
  if (mac) notes.push(`${mac.reason.split(':')[0]} is the Mac game's patch, a different version: it is not used.`);
  return { title: `Lord of Destruction ${r.version}`, problems, advice, notes };
}
